import React, { useMemo } from "react";
import { GeoJson } from "pigeon-maps";
import { sanitizeGeoJsonForMercator } from "../../utils/sanitize-geojson-mercator";
import { MAP_SELECTION_COLOR } from "../../utils/map-selection-glow";

type LatLng = [number, number];

type Props = {
  lat: number;
  lng: number;
  radiusKm: number;
  /** Injected by pigeon-maps when used as a Map child */
  mapState?: {
    width: number;
    height: number;
    center: LatLng;
    zoom: number;
  };
  latLngToPixel?: (
    latLng: LatLng,
    center?: LatLng,
    zoom?: number
  ) => [number, number];
  pixelToLatLng?: (pixel: [number, number]) => LatLng;
};

/** Approx circle as GeoJSON polygon (km radius around lat/lng). */
export function circleFeature(
  lat: number,
  lng: number,
  radiusKm: number,
  steps = 72
) {
  const R = 6371;
  const coords: [number, number][] = [];
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const angDist = radiusKm / R;

  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(latRad) * Math.cos(angDist) +
        Math.cos(latRad) * Math.sin(angDist) * Math.cos(bearing)
    );
    const lng2 =
      lngRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angDist) * Math.cos(latRad),
        Math.cos(angDist) - Math.sin(latRad) * Math.sin(lat2)
      );
    coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }

  return {
    type: "Feature" as const,
    properties: {},
    geometry: {
      type: "Polygon" as const,
      coordinates: [coords],
    },
  };
}

/**
 * Orange explore-radius ring around the selected place.
 */
const ExploreRadiusOverlay: React.FC<Props> = ({
  lat,
  lng,
  radiusKm,
  mapState,
  latLngToPixel,
  pixelToLatLng,
}) => {
  const data = useMemo(() => {
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      !Number.isFinite(radiusKm) ||
      radiusKm <= 0
    ) {
      return null;
    }
    return sanitizeGeoJsonForMercator({
      type: "FeatureCollection",
      features: [circleFeature(lat, lng, radiusKm)],
    });
  }, [lat, lng, radiusKm]);

  if (!data || !mapState?.width || !mapState?.height || !latLngToPixel) {
    return null;
  }

  return (
    <GeoJson
      mapState={mapState}
      latLngToPixel={latLngToPixel}
      pixelToLatLng={pixelToLatLng}
      data={data}
      hover={false}
      styleCallback={() => ({
        fill: `${MAP_SELECTION_COLOR}18`,
        stroke: MAP_SELECTION_COLOR,
        strokeWidth: "2",
        strokeDasharray: "6 4",
        pointerEvents: "none",
        r: "0",
      })}
    />
  );
};

export default ExploreRadiusOverlay;
